import { type MeResponse, getMe } from "@/api/auth";
import { readCachedSession, writeCachedSession } from "@/api/session";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, createContext, useContext, useEffect, useMemo } from "react";

const SessionContext = createContext<MeResponse | null>(null);

export const ME_QUERY_KEY = ["auth", "me"] as const;

export function SessionProvider({
  children,
  renderLoading,
  renderError,
}: {
  children: ReactNode;
  renderLoading: () => ReactNode;
  renderError: (message: string, retry: () => void) => ReactNode;
}) {
  const cached = useMemo(() => readCachedSession(), []);

  const query = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: getMe,
    staleTime: 5 * 60_000,
    initialData: cached ?? undefined,
    initialDataUpdatedAt: 0,
  });

  const session = query.data;

  useEffect(() => {
    if (session) {
      writeCachedSession(session);
    }
  }, [session]);

  if (session) {
    return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
  }

  if (query.isPending) {
    return <>{renderLoading()}</>;
  }

  const message =
    query.error instanceof Error ? query.error.message : "No se pudo cargar tu perfil";
  return <>{renderError(message, () => void query.refetch())}</>;
}

export function useSession(): MeResponse {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error("useSession debe usarse dentro de <SessionProvider>");
  }
  return session;
}
